import { IDockviewPanelProps } from "dockview-react";
import React, { useEffect, useState } from "react";
import { api } from "./utils/api";
import { addFloatingPanel } from "./utils/DockerviewSettings";
import { priceDecimals, RealtimeLwChart, RtChartData } from "./RealtimeLwChart";

export interface Instrument {
  pair_ID: number;
  search_main_text: string;
  search_main_subtext: string;
  search_main_longtext: string;
  type?: string;
  exchange_name?: string;
}

interface InvcomDetails {
  change_val: number | null;
  change_percent_val: number | null;
  exchange_is_open: boolean | null;
  exchange_name: string | null;
  extended_change: number | null;
  extended_change_percent: number | null;
  extended_price: number | null;
  high: number | null;
  low: number | null;
  last: number | null;
  open: number | null;
  volume: string | null;
  last_close_value: number | null;
  last_timestamp: string | null;
  market_status: string | null;
  next_earning_unix_time: string | null;
}

interface ICandleData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

interface IYahooCandle extends ICandleData {
  period?: "pre" | "regular" | "post" | "closed";
}

type ChartSource =
  | { type: "auto"; ticker: string }
  | { type: "manual"; instrument: Instrument };

type ChartTab = "invcom" | "yahoo";

const CANDLE_COLORS = ["#17BECF", "#2ECC71", "#E67E22", "#9B59B6", "#E74C3C"];
const RESOLUTIONS = ["1", "5", "15", "30", "45", "60", "120", "240", "300", "D", "W", "M"];
const DEFAULT_RESOLUTION_IDX = 0; // "1"

const Y_RESOLUTIONS = ["1m", "2m", "5m", "15m", "30m", "60m", "90m", "1d", "1wk", "1mo"];
const Y_DEFAULT_RESOLUTION_IDX = 0; // "1m"

const DATE_PRESETS = [
  { label: "d",   days: 1 },
  { label: "w",   days: 7 },
  { label: "m",   days: 30 },
  { label: "2m",  days: 60 },
  { label: "3m",  days: 90 },
  { label: "6m",  days: 180 },
  { label: "y",   days: 365 },
  { label: "2y",  days: 730 },
  { label: "5y",  days: 1825 },
  { label: "10y", days: 3650 },
];

const NYC_TZ = "America/New_York";

function formatNyc(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: NYC_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const hh = get("hour").replace("24", "00");
  return `${get("year")}-${get("month")}-${get("day")} ${hh}:${get("minute")}`;
}

function presetDates(days: number): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    from: formatNyc(from).split(" ")[0] + " 00:00",
    to: formatNyc(now),
  };
}

function normalizeDate(val: string, suffix: string): string {
  const v = val.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v + suffix : v;
}

function extractCandles(data: any): ICandleData[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.chart_data)) return data.chart_data;
  if (Array.isArray(data?.candles)) return data.candles;
  return [];
}

function buildUrl(source: ChartSource, res: string, fd: string, td: string): string {
  const from = encodeURIComponent(fd);
  const to = encodeURIComponent(td);
  if (source.type === "auto") {
    return `/invcom-auto-chart?ticker=${encodeURIComponent(source.ticker)}&resolution=${res}&from_date=${from}&to_date=${to}`;
  }
  return `/invcom-manual-chart?pair_ID=${source.instrument.pair_ID}&resolution=${res}&from_date=${from}&to_date=${to}`;
}

// /invcom-auto-chart resolves the ticker to a real instrument (pair_ID + full
// name/exchange) server-side — use that instead of the bare typed-in ticker
// so the chart header shows the company name, not just the raw input.
function instrumentFromAutoResponse(ticker: string, tickerData: any): Instrument {
  if (tickerData && tickerData.pair_ID) {
    return {
      pair_ID: tickerData.pair_ID,
      search_main_text: tickerData.search_main_text || ticker,
      search_main_subtext: tickerData.search_main_subtext || "",
      search_main_longtext: tickerData.search_main_longtext || "",
    };
  }
  return {
    pair_ID: 0,
    search_main_text: ticker,
    search_main_subtext: "",
    search_main_longtext: "",
  };
}

export const RealtimeChart = (props: IDockviewPanelProps) => {
  // default view: 1-minute bars over the last day
  const initialWeek = presetDates(1);

  // Track 1
  const [ticker, setTicker] = useState("");
  const [autoSelect, setAutoSelect] = useState(true);
  const [instrumentList, setInstrumentList] = useState<Instrument[] | null>(
    null,
  );
  const [candles1, setCandles1] = useState<ICandleData[] | null>(null);
  const [displayInst1, setDisplayInst1] = useState<Instrument | null>(null);
  const [source1, setSource1] = useState<ChartSource | null>(null);
  const [risingColor, setRisingColor] = useState(CANDLE_COLORS[0]);

  // Track 2
  const [track2Enabled, setTrack2Enabled] = useState(false);
  const [ticker2, setTicker2] = useState("");
  const [autoSelect2, setAutoSelect2] = useState(true);
  const [instrumentList2, setInstrumentList2] = useState<Instrument[] | null>(
    null,
  );
  const [candles2, setCandles2] = useState<ICandleData[] | null>(null);
  const [displayInst2, setDisplayInst2] = useState<Instrument | null>(null);
  const [source2, setSource2] = useState<ChartSource | null>(null);
  const [risingColor2, setRisingColor2] = useState(CANDLE_COLORS[1]);

  // Date / resolution
  const [fromDate, setFromDate] = useState(initialWeek.from);
  const [toDate, setToDate] = useState(initialWeek.to);
  const [selectedPreset, setSelectedPreset] = useState("d");
  const [resolutionIdx, setResolutionIdx] = useState(DEFAULT_RESOLUTION_IDX);
  const [compress, setCompress] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const [errorMessage, setErrorMessage] = useState("");
  const [plotData, setPlotData] = useState<RtChartData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState<InvcomDetails | null>(null);
  const [detailsPairId, setDetailsPairId] = useState<number | null>(null);

  const resolution = RESOLUTIONS[resolutionIdx];

  // --- Source tab (Invcom / Yahoo) ---
  const [activeTab, setActiveTab] = useState<ChartTab>("invcom");

  // --- Yahoo tab state (ticker, dates, preset, compress, track2 shared with Invcom above) ---
  const [yCandles1, setYCandles1] = useState<IYahooCandle[] | null>(null);
  const [yDisplayInst1, setYDisplayInst1] = useState<Instrument | null>(null);
  const [yRisingColor, setYRisingColor] = useState(CANDLE_COLORS[0]);

  const [yCandles2, setYCandles2] = useState<IYahooCandle[] | null>(null);
  const [yDisplayInst2, setYDisplayInst2] = useState<Instrument | null>(null);
  const [yRisingColor2, setYRisingColor2] = useState(CANDLE_COLORS[1]);

  const [yResolutionIdx, setYResolutionIdx] = useState(
    Y_DEFAULT_RESOLUTION_IDX,
  );
  const [yRefreshCount, setYRefreshCount] = useState(0);

  const [yErrorMessage, setYErrorMessage] = useState("");
  const [yPlotData, setYPlotData] = useState<RtChartData | null>(null);
  const [yIsLoading, setYIsLoading] = useState(false);
  const [yDetails, setYDetails] = useState<InvcomDetails | null>(null);
  const [yDetailsTicker, setYDetailsTicker] = useState<string | null>(null);

  const yResolution = Y_RESOLUTIONS[yResolutionIdx];

  // --- Plot builders ---
  // (compress, % normalization and pre/post market shading are done by RealtimeLwChart)

  function buildSinglePlot(candles: ICandleData[], color: string) {
    setPlotData({ tracks: [{ name: "Price", candles, color }] });
  }

  function buildComparisonPlot(
    c1: ICandleData[],
    c2: ICandleData[],
    name1: string,
    name2: string,
    color1: string,
    color2: string,
  ) {
    if (!c1.length || !c2.length) return;
    setPlotData({
      tracks: [
        { name: name1, candles: c1, color: color1 },
        { name: name2, candles: c2, color: color2 },
      ],
    });
  }

  // --- Yahoo plot builders ---

  function buildYSinglePlot(candles: IYahooCandle[], color: string) {
    setYPlotData({ tracks: [{ name: "Price", candles, color }] });
  }

  function buildYComparisonPlot(
    c1: IYahooCandle[],
    c2: IYahooCandle[],
    name1: string,
    name2: string,
    color1: string,
    color2: string,
  ) {
    if (!c1.length || !c2.length) return;
    setYPlotData({
      tracks: [
        { name: name1, candles: c1, color: color1 },
        { name: name2, candles: c2, color: color2 },
      ],
    });
  }

  // Rebuild comparison whenever candles or colors change in track2 mode
  useEffect(() => {
    if (track2Enabled && candles1 && candles2) {
      buildComparisonPlot(
        candles1,
        candles2,
        displayInst1?.search_main_text ?? "Track 1",
        displayInst2?.search_main_text ?? "Track 2",
        risingColor,
        risingColor2,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candles1,
    candles2,
    track2Enabled,
    risingColor,
    risingColor2,
    displayInst1,
    displayInst2,
  ]);

  // Update rising color in single mode without refetch
  useEffect(() => {
    if (!track2Enabled) {
      setPlotData((prev) =>
        prev?.tracks.length === 1
          ? { tracks: [{ ...prev.tracks[0], color: risingColor }] }
          : prev,
      );
    }
  }, [risingColor, track2Enabled]);

  // Rebuild Yahoo comparison whenever candles or colors change in track2 mode
  useEffect(() => {
    if (track2Enabled && yCandles1 && yCandles2) {
      buildYComparisonPlot(
        yCandles1,
        yCandles2,
        yDisplayInst1?.search_main_text ?? "Track 1",
        yDisplayInst2?.search_main_text ?? "Track 2",
        yRisingColor,
        yRisingColor2,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    yCandles1,
    yCandles2,
    track2Enabled,
    yRisingColor,
    yRisingColor2,
    yDisplayInst1,
    yDisplayInst2,
  ]);

  // Update Yahoo rising color in single mode without refetch
  useEffect(() => {
    if (!track2Enabled) {
      setYPlotData((prev) =>
        prev?.tracks.length === 1
          ? { tracks: [{ ...prev.tracks[0], color: yRisingColor }] }
          : prev,
      );
    }
  }, [yRisingColor, track2Enabled]);

  // Keyboard navigation → change resolution and refetch (Invcom tab)
  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (activeTab !== "invcom") return;
      const isHoriz = e.key === "ArrowRight" || e.key === "ArrowLeft";
      const isVert = e.key === "ArrowUp" || e.key === "ArrowDown";
      if (!isHoriz && !isVert) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as HTMLElement)?.isContentEditable
      )
        return;
      if (props.containerApi.activePanel?.id !== props.api.id) return;
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();
      if (isHoriz) {
        const next =
          e.key === "ArrowRight"
            ? (resolutionIdx + 1) % RESOLUTIONS.length
            : (resolutionIdx - 1 + RESOLUTIONS.length) % RESOLUTIONS.length;
        changeResolution(next);
      } else {
        const currentPresetIdx = DATE_PRESETS.findIndex(
          (p) => p.label === selectedPreset,
        );
        const next =
          e.key === "ArrowUp"
            ? (currentPresetIdx + 1) % DATE_PRESETS.length
            : (currentPresetIdx - 1 + DATE_PRESETS.length) %
              DATE_PRESETS.length;
        const { label, days } = DATE_PRESETS[next];
        const { from, to } = presetDates(days);
        setSelectedPreset(label);
        setFromDate(from);
        setToDate(to);
        changeDates(from, to, label);
      }
    };
    window.addEventListener("keydown", keyHandler, { capture: true });
    return () =>
      window.removeEventListener("keydown", keyHandler, {
        capture: true,
      } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    props.api.id,
    props.containerApi,
    resolutionIdx,
    selectedPreset,
    source1,
    source2,
    track2Enabled,
    fromDate,
    toDate,
    risingColor,
    risingColor2,
  ]);

  // Keyboard navigation → change resolution and refetch (Yahoo tab)
  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (activeTab !== "yahoo") return;
      const isHoriz = e.key === "ArrowRight" || e.key === "ArrowLeft";
      const isVert = e.key === "ArrowUp" || e.key === "ArrowDown";
      if (!isHoriz && !isVert) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (e.target as HTMLElement)?.isContentEditable
      )
        return;
      if (props.containerApi.activePanel?.id !== props.api.id) return;
      e.preventDefault();
      e.stopPropagation();
      (e as any).stopImmediatePropagation?.();
      if (isHoriz) {
        const next =
          e.key === "ArrowRight"
            ? (yResolutionIdx + 1) % Y_RESOLUTIONS.length
            : (yResolutionIdx - 1 + Y_RESOLUTIONS.length) %
              Y_RESOLUTIONS.length;
        changeYResolution(next);
      } else {
        const currentPresetIdx = DATE_PRESETS.findIndex(
          (p) => p.label === selectedPreset,
        );
        const next =
          e.key === "ArrowUp"
            ? (currentPresetIdx + 1) % DATE_PRESETS.length
            : (currentPresetIdx - 1 + DATE_PRESETS.length) %
              DATE_PRESETS.length;
        const { label, days } = DATE_PRESETS[next];
        const { from, to } = presetDates(days);
        changeYDates(from, to, label);
      }
    };
    window.addEventListener("keydown", keyHandler, { capture: true });
    return () =>
      window.removeEventListener("keydown", keyHandler, {
        capture: true,
      } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    props.api.id,
    props.containerApi,
    yResolutionIdx,
    selectedPreset,
    ticker,
    ticker2,
    track2Enabled,
    fromDate,
    toDate,
    yRisingColor,
    yRisingColor2,
  ]);

  // Params-based auto-load (from Watchlist)
  useEffect(() => {
    const fd = normalizeDate(
      props.params?.startDate ?? initialWeek.from,
      " 00:00",
    );
    const td = normalizeDate(props.params?.endDate ?? initialWeek.to, " 23:59");
    const resIdx = props.params?.interval
      ? RESOLUTIONS.indexOf(String(props.params.interval))
      : DEFAULT_RESOLUTION_IDX;
    const resolvedResIdx = resIdx === -1 ? DEFAULT_RESOLUTION_IDX : resIdx;
    const res = RESOLUTIONS[resolvedResIdx];

    if (fd !== initialWeek.from) setFromDate(fd);
    if (td !== initialWeek.to) setToDate(td);
    if (resolvedResIdx !== DEFAULT_RESOLUTION_IDX)
      setResolutionIdx(resolvedResIdx);

    const tickerFromParams = (
      props.params?.ticker as string | undefined
    )?.trim();
    if (tickerFromParams) {
      setTicker(tickerFromParams);
      if (fd !== initialWeek.from) setFromDate(fd);
      if (td !== initialWeek.to) setToDate(td);
      fetchYWithDatesAndRes(yResolution, fd, td, tickerFromParams);
    }

    if (props.params?.instrument_id) {
      const type = props.params.type as string | undefined;
      const exchangeName = props.params.exchange_name as string | undefined;
      const subtext =
        [type, exchangeName].filter(Boolean).join(" · ") || "from Watchlist";
      const inst: Instrument = {
        pair_ID: props.params.instrument_id,
        search_main_text: props.params.ticker || "",
        search_main_subtext: subtext,
        search_main_longtext: props.params.company_name || "",
      };
      setTicker(inst.search_main_text);
      const src: ChartSource = { type: "manual", instrument: inst };
      setSource1(src);
      setDisplayInst1(inst);
      setDetailsPairId(inst.pair_ID);
      api
        .get(buildUrl(src, res, fd, td))
        .then((r) => {
          const c = extractCandles(r.data);
          setCandles1(c);
          buildSinglePlot(c, risingColor);
        })
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.message || "Error loading chart.",
          ),
        );
    } else if (props.params?.ticker) {
      const t = props.params.ticker as string;
      setTicker(t);
      setAutoSelect(true);
      const src: ChartSource = { type: "auto", ticker: t };
      setSource1(src);
      api
        .get(buildUrl(src, res, fd, td))
        .then((r) => {
          const c = extractCandles(r.data);
          setDisplayInst1(instrumentFromAutoResponse(t, r.data?.ticker_data));
          setCandles1(c);
          buildSinglePlot(c, risingColor);
          const pairId = r.data?.ticker_data?.pair_ID;
          if (pairId) setDetailsPairId(pairId);
        })
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.message || "Error loading chart.",
          ),
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.params?.ticker, props.params?.instrument_id]);

  // Poll /invcom-details every 1s whenever we have a pair_ID
  useEffect(() => {
    if (!detailsPairId) {
      setDetails(null);
      return;
    }
    const fetchDetails = () => {
      api
        .get(`/invcom-details?pair_ID=${detailsPairId}`)
        .then((r) => setDetails(r.data))
        .catch(() => {});
    };
    fetchDetails();
    const interval = setInterval(fetchDetails, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsPairId]);

  // --- Price alert (invcom instrument, set from the Invcom or Yahoo chart): "Alert" arms a click-to-set on the chart; one alert per ticker ---
  const [alertArmed, setAlertArmed] = useState(false);
  const [priceAlert, setPriceAlert] = useState<{ target_price: number } | null>(
    null,
  );

  // Load this instrument's alert, and keep polling so a fired (deleted) alert resets the button
  useEffect(() => {
    setAlertArmed(false);
    setPriceAlert(null);
    if (!detailsPairId) return;
    const fetchAlert = () => {
      api
        .get(`/price-alert?investing_id=${detailsPairId}`)
        .then((r) => setPriceAlert(r.data ?? null))
        .catch(() => {});
    };
    fetchAlert();
    const interval = setInterval(fetchAlert, 5000);
    return () => clearInterval(interval);
  }, [detailsPairId]);

  function handleAlertButton() {
    if (priceAlert && detailsPairId) {
      api
        .delete(`/price-alert?investing_id=${detailsPairId}`)
        .then(() => setPriceAlert(null))
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.detail || "Error removing alert.",
          ),
        );
    } else {
      setAlertArmed((armed) => !armed);
    }
  }

  function handleChartPriceClick(price: number) {
    if (!alertArmed || !detailsPairId) return;
    const target = Number(price.toFixed(priceDecimals(price)));
    setAlertArmed(false);
    api
      .post("/price-alert", {
        investing_id: detailsPairId,
        ticker: displayInst1?.search_main_text || ticker,
        target_price: target,
      })
      .then((r) => setPriceAlert(r.data))
      .catch((err) =>
        setErrorMessage(err.response?.data?.detail || "Error setting alert."),
      );
  }

  // Poll /yahoo-details every 1s whenever a Yahoo ticker is loaded (1:1 with /invcom-details above)
  useEffect(() => {
    if (!yDetailsTicker) {
      setYDetails(null);
      return;
    }
    const fetchYDetails = () => {
      api
        .get(`/yahoo-details?ticker=${encodeURIComponent(yDetailsTicker)}`)
        .then((r) => setYDetails(r.data))
        .catch(() => {});
    };
    fetchYDetails();
    const interval = setInterval(fetchYDetails, 1000);
    return () => clearInterval(interval);
  }, [yDetailsTicker]);

  // --- Resolution change (arrows or radio click) ---

  function fetchWithDatesAndRes(res: string, fd: string, td: string) {
    setErrorMessage("");
    setIsLoading(true);

    const p1 = source1
      ? api
          .get(buildUrl(source1, res, fd, td))
          .then((r) => {
            const c = extractCandles(r.data);
            setCandles1(c);
            if (!track2Enabled) buildSinglePlot(c, risingColor);
          })
          .catch((err) =>
            setErrorMessage(
              err.response?.data?.message || "Error fetching chart.",
            ),
          )
      : Promise.resolve();

    const p2 =
      track2Enabled && source2
        ? api
            .get(buildUrl(source2, res, fd, td))
            .then((r) => setCandles2(extractCandles(r.data)))
            .catch((err) =>
              setErrorMessage(
                err.response?.data?.message || "Error fetching track 2.",
              ),
            )
        : Promise.resolve();

    Promise.all([p1, p2]).finally(() => setIsLoading(false));
  }

  function changeResolution(idx: number) {
    setResolutionIdx(idx);
    fetchWithDatesAndRes(RESOLUTIONS[idx], fromDate, toDate);
  }

  function changeDates(fd: string, td: string, preset: string) {
    setSelectedPreset(preset);
    setFromDate(fd);
    setToDate(td);
    fetchWithDatesAndRes(resolution, fd, td);
    fetchYWithDatesAndRes(yResolution, fd, td);
  }

  function handleRefresh() {
    const refreshedTo = formatNyc(new Date(Date.now() + 60_000));
    setSelectedPreset("");
    setToDate(refreshedTo);
    setRefreshCount((n) => n + 1);
    fetchWithDatesAndRes(resolution, fromDate, refreshedTo);
    setYRefreshCount((n) => n + 1);
    fetchYWithDatesAndRes(yResolution, fromDate, refreshedTo);
  }

  function openTwitterSearch(t: string) {
    const clean = t.trim().replace(/^\$/, "");
    if (!clean) return;
    addFloatingPanel(props.containerApi, "twitter_realtime", {
      query: `$${clean.toLowerCase()}`,
    });
  }

  // opens a new IBKR chat window that sends the ticker as its first message
  function openIbFast(t: string) {
    const clean = t.trim().replace(/^\$/, "").toUpperCase();
    if (!clean) return;
    addFloatingPanel(props.containerApi, "ibkr_chat", { message: clean });
  }

  // Alert + IB Fast, shown in the toolbar of both tabs. The alert is always the
  // invcom instrument's (checked against invcom prices); on Yahoo it's usable
  // when the Invcom side has the same ticker loaded.
  function renderAlertAndIbFast(symbol: string, alertPairId: number | null) {
    const alertOff = !alertPairId;
    return (
      <>
        <button
          type="button"
          onClick={handleAlertButton}
          disabled={alertOff}
          className={`btn text-xs ${alertArmed ? "btn-danger" : "btn-default"}`}
          title={
            alertOff
              ? "No Invcom instrument for this ticker - load it on the Invcom tab"
              : priceAlert
                ? "Remove this price alert"
                : alertArmed
                  ? "Click a price level on the chart to set the alert"
                  : "Set a price alert by clicking a level on the chart"
          }
        >
          {priceAlert
            ? `${priceAlert.target_price.toFixed(priceDecimals(priceAlert.target_price))} cancel`
            : alertArmed
              ? "Cancel"
              : "Alert"}
        </button>

        <button
          type="button"
          onClick={() => openIbFast(symbol)}
          disabled={!symbol.trim()}
          className="btn btn-default text-xs"
          title={`Open IBKR Chat with ${symbol.trim().toUpperCase() || "the ticker"}`}
        >
          IB Fast
        </button>
      </>
    );
  }

  // the Yahoo tab's alert targets the invcom instrument only when both show the same ticker
  const yAlertPairId =
    detailsPairId &&
    yDisplayInst1 &&
    displayInst1 &&
    yDisplayInst1.search_main_text.toUpperCase() ===
      displayInst1.search_main_text.toUpperCase()
      ? detailsPairId
      : null;

  // --- Yahoo: URL / fetch helpers ---

  function buildYUrl(
    ticker: string,
    interval: string,
    fd: string,
    td: string,
  ): string {
    const from = encodeURIComponent(fd);
    const to = encodeURIComponent(td);
    return `/yahoo-chart?ticker=${encodeURIComponent(ticker.toUpperCase())}&interval=${interval}&from_date=${from}&to_date=${to}`;
  }

  function instrumentFromYahooResponse(
    ticker: string,
    tickerData: any,
  ): Instrument {
    const subtext = [
      tickerData?.fullExchangeName,
      tickerData?.instrumentType,
      tickerData?.currency,
    ]
      .filter(Boolean)
      .join(" - ");
    return {
      pair_ID: 0,
      search_main_text: tickerData?.symbol || ticker.toUpperCase(),
      search_main_subtext: subtext,
      search_main_longtext: tickerData?.shortName || tickerData?.longName || "",
    };
  }

  function fetchYWithDatesAndRes(interval: string, fd: string, td: string, tickerOverride?: string) {
    const t1 = (tickerOverride ?? ticker).trim();
    setYErrorMessage("");
    setYIsLoading(true);

    const p1 = t1
      ? api
          .get(buildYUrl(t1, interval, fd, td))
          .then((r) => {
            const c = extractCandles(r.data) as IYahooCandle[];
            setYCandles1(c);
            setYDetails(r.data?.details ?? null);
            setYDetailsTicker(
              r.data?.ticker_data?.symbol || t1.toUpperCase(),
            );
            setYDisplayInst1(
              instrumentFromYahooResponse(t1, r.data?.ticker_data),
            );
            if (!track2Enabled) buildYSinglePlot(c, yRisingColor);
          })
          .catch((err) =>
            setYErrorMessage(
              err.response?.data?.message || "Error fetching chart.",
            ),
          )
      : Promise.resolve();

    const p2 =
      track2Enabled && ticker2.trim()
        ? api
            .get(buildYUrl(ticker2.trim(), interval, fd, td))
            .then((r) => {
              setYCandles2(extractCandles(r.data) as IYahooCandle[]);
              setYDisplayInst2(
                instrumentFromYahooResponse(
                  ticker2.trim(),
                  r.data?.ticker_data,
                ),
              );
            })
            .catch((err) =>
              setYErrorMessage(
                err.response?.data?.message || "Error fetching track 2.",
              ),
            )
        : Promise.resolve();

    Promise.all([p1, p2]).finally(() => setYIsLoading(false));
  }

  function changeYResolution(idx: number) {
    setYResolutionIdx(idx);
    fetchYWithDatesAndRes(Y_RESOLUTIONS[idx], fromDate, toDate);
  }

  function changeYDates(fd: string, td: string, preset: string) {
    setSelectedPreset(preset);
    setFromDate(fd);
    setToDate(td);
    fetchYWithDatesAndRes(yResolution, fd, td);
    if (source1) fetchWithDatesAndRes(resolution, fd, td);
  }

  function handleYSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (ticker.trim() === "") {
      setYErrorMessage("Please enter a ticker symbol.");
      return;
    }
    if (track2Enabled && ticker2.trim() === "") {
      setYErrorMessage("Please enter a ticker for track 2.");
      return;
    }

    const fd = normalizeDate(fromDate, " 00:00");
    const td = normalizeDate(toDate, " 23:59");
    setFromDate(fd);
    setToDate(td);

    setYCandles1(null);
    setYCandles2(null);
    setYDisplayInst1(null);
    setYDisplayInst2(null);
    setYPlotData(null);
    setYErrorMessage("");
    setYDetails(null);
    setYDetailsTicker(null);

    fetchYWithDatesAndRes(yResolution, fd, td);
  }

  function handleYRefresh() {
    const refreshedTo = formatNyc(new Date(Date.now() + 60_000));
    setSelectedPreset("");
    setToDate(refreshedTo);
    setYRefreshCount((n) => n + 1);
    fetchYWithDatesAndRes(yResolution, fromDate, refreshedTo);
    setRefreshCount((n) => n + 1);
    if (source1) fetchWithDatesAndRes(resolution, fromDate, refreshedTo);
  }

  // --- Form submit ---

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (ticker.trim() === "") {
      setErrorMessage("Please enter a ticker symbol.");
      return;
    }
    if (track2Enabled && ticker2.trim() === "") {
      setErrorMessage("Please enter a ticker for track 2.");
      return;
    }

    const fd = normalizeDate(fromDate, " 00:00");
    const td = normalizeDate(toDate, " 23:59");
    setFromDate(fd);
    setToDate(td);

    setCandles1(null);
    setCandles2(null);
    setDisplayInst1(null);
    setDisplayInst2(null);
    setInstrumentList(null);
    setInstrumentList2(null);
    setSource1(null);
    setSource2(null);
    setPlotData(null);
    setErrorMessage("");
    setDetailsPairId(null);

    // Track 1
    if (autoSelect) {
      const src: ChartSource = { type: "auto", ticker: ticker.trim() };
      setSource1(src);
      api
        .get(buildUrl(src, resolution, fd, td))
        .then((r) => {
          const c = extractCandles(r.data);
          setDisplayInst1(
            instrumentFromAutoResponse(ticker.trim(), r.data?.ticker_data),
          );
          setCandles1(c);
          if (!track2Enabled) buildSinglePlot(c, risingColor);
          const pairId = r.data?.ticker_data?.pair_ID;
          if (pairId) setDetailsPairId(pairId);
        })
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.message || "Error fetching chart.",
          ),
        );
    } else {
      api
        .get(`/invcom-search?ticker=${encodeURIComponent(ticker.trim())}`)
        .then((r) => setInstrumentList(r.data))
        .catch((err) =>
          setErrorMessage(err.response?.data?.message || "Error searching."),
        );
    }

    // Track 2
    if (track2Enabled) {
      if (autoSelect2) {
        const src: ChartSource = { type: "auto", ticker: ticker2.trim() };
        setSource2(src);
        api
          .get(buildUrl(src, resolution, fd, td))
          .then((r) => {
            const c = extractCandles(r.data);
            setDisplayInst2(
              instrumentFromAutoResponse(ticker2.trim(), r.data?.ticker_data),
            );
            setCandles2(c);
          })
          .catch((err) =>
            setErrorMessage(
              err.response?.data?.message || "Error fetching track 2.",
            ),
          );
      } else {
        api
          .get(`/invcom-search?ticker=${encodeURIComponent(ticker2.trim())}`)
          .then((r) => setInstrumentList2(r.data))
          .catch((err) =>
            setErrorMessage(
              err.response?.data?.message || "Error searching track 2.",
            ),
          );
      }
    }

    // Also fetch Yahoo tab data with the same ticker/dates
    setYCandles1(null);
    setYCandles2(null);
    setYDisplayInst1(null);
    setYDisplayInst2(null);
    setYPlotData(null);
    setYErrorMessage("");
    setYDetails(null);
    setYDetailsTicker(null);
    fetchYWithDatesAndRes(yResolution, fd, td);
  }

  // --- Pick instrument from search results ---

  function pickInstrument(instrument: Instrument, track: 1 | 2) {
    const src: ChartSource = { type: "manual", instrument };
    setErrorMessage("");

    if (track === 1) {
      setInstrumentList(null);
      setSource1(src);
      setDisplayInst1(instrument);
      setDetailsPairId(instrument.pair_ID);
      api
        .get(buildUrl(src, resolution, fromDate, toDate))
        .then((r) => {
          const c = extractCandles(r.data);
          setCandles1(c);
          if (!track2Enabled) buildSinglePlot(c, risingColor);
        })
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.message || "Error fetching chart.",
          ),
        );
    } else {
      setInstrumentList2(null);
      setSource2(src);
      setDisplayInst2(instrument);
      api
        .get(buildUrl(src, resolution, fromDate, toDate))
        .then((r) => setCandles2(extractCandles(r.data)))
        .catch((err) =>
          setErrorMessage(
            err.response?.data?.message || "Error fetching track 2.",
          ),
        );
    }
  }

  const bothLoaded = track2Enabled
    ? candles1 !== null && candles2 !== null
    : candles1 !== null;

  const yBothLoaded = track2Enabled
    ? yCandles1 !== null && yCandles2 !== null
    : yCandles1 !== null;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 flex items-center gap-4 px-4 py-1.5 border-b border-gray-200">
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name={`chart-source-tab-${props.api.id}`}
            value="invcom"
            checked={activeTab === "invcom"}
            onChange={() => setActiveTab("invcom")}
          />
          Invcom
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-sm">
          <input
            type="radio"
            name={`chart-source-tab-${props.api.id}`}
            value="yahoo"
            checked={activeTab === "yahoo"}
            onChange={() => setActiveTab("yahoo")}
          />
          Yahoo
        </label>
      </div>

      <div
        className={`flex-1 p-4 overflow-auto ${activeTab !== "invcom" ? "hidden" : ""}`}
      >
        {errorMessage && (
          <div className="text-red-500 mb-4">{errorMessage}</div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {/* Date + resolution row */}
          <div className="flex gap-4 items-center flex-wrap">
            {/* Date presets */}
            <div className="flex gap-1 items-center flex-wrap">
              {DATE_PRESETS.map(({ label, days }) => (
                <label
                  key={label}
                  className="flex items-center gap-0.5 cursor-pointer text-xs"
                >
                  <input
                    type="radio"
                    name="tv-preset"
                    value={label}
                    checked={selectedPreset === label}
                    onChange={() => {
                      const { from, to } = presetDates(days);
                      changeDates(from, to, label);
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>

            <label className="flex flex-col gap-0.5">
              <span className="text-xs text-gray-400">From Date</span>
              <input
                placeholder="YYYY-MM-DD HH:MM"
                type="text"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setSelectedPreset("");
                }}
              />
            </label>

            <label className="flex flex-col gap-0.5">
              <span className="text-xs text-gray-400">To Date</span>
              <input
                placeholder="YYYY-MM-DD HH:MM"
                type="text"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setSelectedPreset("");
                }}
              />
            </label>

            {/* Resolution radio buttons */}
            <div className="flex gap-1 items-center flex-wrap">
              <span className="text-xs text-gray-400 mr-1">Res:</span>
              {RESOLUTIONS.map((r, i) => (
                <label
                  key={r}
                  className="flex items-center gap-0.5 cursor-pointer text-xs"
                >
                  <input
                    type="radio"
                    name="tv-resolution"
                    value={r}
                    checked={resolutionIdx === i}
                    onChange={() => changeResolution(i)}
                  />
                  {r}
                </label>
              ))}
            </div>

            {/* Compress toggle */}
            <label className="flex items-center gap-1 cursor-pointer text-xs">
              <span className="text-gray-400">Compress:</span>
              <input
                type="checkbox"
                checked={compress}
                onChange={(e) => setCompress(e.target.checked)}
              />
            </label>

            <button
              type="button"
              onClick={handleRefresh}
              className="btn btn-default text-xs"
              title="Refetch with 'To Date' reset to now + 1 minute"
            >
              Refresh
            </button>

            <button
              type="button"
              onClick={() =>
                openTwitterSearch(displayInst1?.search_main_text || ticker)
              }
              className="btn btn-default text-xs"
              title="Open Twitter Real-time searching for $TICKER"
            >
              Twitter
            </button>

            {renderAlertAndIbFast(
              displayInst1?.search_main_text || ticker,
              detailsPairId,
            )}
          </div>

          {/* Track 1 */}
          <div className="flex gap-6 items-center">
            <div className="w-48">
              <input
                placeholder="AAPL"
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value)}
              />
            </div>

            <div className="flex gap-2 items-center">
              <label htmlFor="autoSelect">Auto Select</label>
              <input
                type="checkbox"
                id="autoSelect"
                checked={autoSelect}
                onChange={(e) => setAutoSelect(e.target.checked)}
              />
            </div>

            <div className="flex gap-1 items-center">
              {CANDLE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => setRisingColor(color)}
                  style={{ backgroundColor: color }}
                  className={`w-4 h-4 rounded-full border-2 ${
                    risingColor === color
                      ? "border-gray-800"
                      : "border-transparent"
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2 items-center">
              <label
                htmlFor="track2Toggle"
                className="text-sm cursor-pointer select-none"
              >
                +Track
              </label>
              <input
                type="checkbox"
                id="track2Toggle"
                checked={track2Enabled}
                onChange={(e) => {
                  setTrack2Enabled(e.target.checked);
                  if (!e.target.checked) {
                    setCandles2(null);
                    setDisplayInst2(null);
                    setInstrumentList2(null);
                    setTicker2("");
                    setSource2(null);
                    if (candles1) buildSinglePlot(candles1, risingColor);
                    else setPlotData(null);
                  }
                }}
              />
            </div>

          </div>

          {/* Track 2 */}
          {track2Enabled && (
            <div className="flex gap-6 items-center flex-wrap">
              <div className="w-48">
                <input
                  placeholder="TSLA"
                  type="text"
                  value={ticker2}
                  onChange={(e) => setTicker2(e.target.value)}
                />
              </div>

              <div className="flex gap-2 items-center">
                <label htmlFor="autoSelect2">Auto Select</label>
                <input
                  type="checkbox"
                  id="autoSelect2"
                  checked={autoSelect2}
                  onChange={(e) => setAutoSelect2(e.target.checked)}
                />
              </div>

              <div className="flex gap-1 items-center">
                {CANDLE_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    title={color}
                    onClick={() => setRisingColor2(color)}
                    style={{ backgroundColor: color }}
                    className={`w-4 h-4 rounded-full border-2 ${
                      risingColor2 === color
                        ? "border-gray-800"
                        : "border-transparent"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          <button className="btn btn-default hidden" type="submit">
            Load
          </button>
        </form>

        {/* Search results track 1 */}
        {instrumentList && (
          <div className="mt-4">
            <h2>Search Results{track2Enabled ? " (Track 1)" : ""}:</h2>
            {instrumentList.map((inst) => (
              <div key={inst.pair_ID} className="mb-2">
                <button
                  type="button"
                  onClick={() => pickInstrument(inst, 1)}
                  className="cursor-pointer text-left"
                >
                  {inst.pair_ID}: [{inst.search_main_text}]{" "}
                  {inst.search_main_longtext} ({inst.search_main_subtext})
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Search results track 2 */}
        {instrumentList2 && (
          <div className="mt-4">
            <h2>Search Results (Track 2):</h2>
            {instrumentList2.map((inst) => (
              <div key={inst.pair_ID} className="mb-2">
                <button
                  type="button"
                  onClick={() => pickInstrument(inst, 2)}
                  className="cursor-pointer text-left"
                >
                  {inst.pair_ID}: [{inst.search_main_text}]{" "}
                  {inst.search_main_longtext} ({inst.search_main_subtext})
                </button>
              </div>
            ))}
          </div>
        )}

        {displayInst1 && (bothLoaded || isLoading) && (
          <Plotter
            key={`${displayInst1.pair_ID}-${displayInst2?.pair_ID ?? "none"}-${resolutionIdx}`}
            data={plotData}
            instrument={displayInst1}
            instrument2={track2Enabled ? displayInst2 : null}
            resolution={resolution}
            isCompareMode={track2Enabled}
            isLoading={isLoading}
            compress={compress}
            refreshCount={refreshCount}
            lastPrice={details?.extended_price ?? details?.last}
            alertPrice={priceAlert?.target_price ?? null}
            onPriceClick={alertArmed ? handleChartPriceClick : undefined}
          />
        )}

        {details && <DetailsBar details={details} />}
      </div>

      <div
        className={`flex-1 p-4 overflow-auto ${activeTab !== "yahoo" ? "hidden" : ""}`}
      >
        {yErrorMessage && (
          <div className="text-red-500 mb-4">{yErrorMessage}</div>
        )}

        <form onSubmit={handleYSubmit} className="flex flex-col gap-3">
          {/* Date + resolution row */}
          <div className="flex gap-4 items-center flex-wrap">
            {/* Date presets */}
            <div className="flex gap-1 items-center flex-wrap">
              {DATE_PRESETS.map(({ label, days }) => (
                <label
                  key={label}
                  className="flex items-center gap-0.5 cursor-pointer text-xs"
                >
                  <input
                    type="radio"
                    name="yahoo-preset"
                    value={label}
                    checked={selectedPreset === label}
                    onChange={() => {
                      const { from, to } = presetDates(days);
                      changeYDates(from, to, label);
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>

            <label className="flex flex-col gap-0.5">
              <span className="text-xs text-gray-400">From Date</span>
              <input
                placeholder="YYYY-MM-DD HH:MM"
                type="text"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setSelectedPreset("");
                }}
              />
            </label>

            <label className="flex flex-col gap-0.5">
              <span className="text-xs text-gray-400">To Date</span>
              <input
                placeholder="YYYY-MM-DD HH:MM"
                type="text"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setSelectedPreset("");
                }}
              />
            </label>

            {/* Resolution radio buttons */}
            <div className="flex gap-1 items-center flex-wrap">
              <span className="text-xs text-gray-400 mr-1">Res:</span>
              {Y_RESOLUTIONS.map((r, i) => (
                <label
                  key={r}
                  className="flex items-center gap-0.5 cursor-pointer text-xs"
                >
                  <input
                    type="radio"
                    name="yahoo-resolution"
                    value={r}
                    checked={yResolutionIdx === i}
                    onChange={() => changeYResolution(i)}
                  />
                  {r}
                </label>
              ))}
            </div>

            {/* Compress toggle */}
            <label className="flex items-center gap-1 cursor-pointer text-xs">
              <span className="text-gray-400">Compress:</span>
              <input
                type="checkbox"
                checked={compress}
                onChange={(e) => setCompress(e.target.checked)}
              />
            </label>

            <button
              type="button"
              onClick={handleYRefresh}
              className="btn btn-default text-xs"
              title="Refetch with 'To Date' reset to now + 1 minute"
            >
              Refresh
            </button>

            <button
              type="button"
              onClick={() =>
                openTwitterSearch(yDisplayInst1?.search_main_text || ticker)
              }
              className="btn btn-default text-xs"
              title="Open Twitter Real-time searching for $TICKER"
            >
              Twitter
            </button>

            {renderAlertAndIbFast(
              yDisplayInst1?.search_main_text || ticker,
              yAlertPairId,
            )}
          </div>

          {/* Track 1 */}
          <div className="flex gap-6 items-center">
            <div className="w-48">
              <input
                placeholder="AAPL"
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value)}
              />
            </div>

            <div className="flex gap-1 items-center">
              {CANDLE_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  title={color}
                  onClick={() => setYRisingColor(color)}
                  style={{ backgroundColor: color }}
                  className={`w-4 h-4 rounded-full border-2 ${
                    yRisingColor === color
                      ? "border-gray-800"
                      : "border-transparent"
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2 items-center">
              <label
                htmlFor="yTrack2Toggle"
                className="text-sm cursor-pointer select-none"
              >
                +Track
              </label>
              <input
                type="checkbox"
                id="yTrack2Toggle"
                checked={track2Enabled}
                onChange={(e) => {
                  setTrack2Enabled(e.target.checked);
                  if (!e.target.checked) {
                    setYCandles2(null);
                    setYDisplayInst2(null);
                    setTicker2("");
                    if (yCandles1) buildYSinglePlot(yCandles1, yRisingColor);
                    else setYPlotData(null);
                  }
                }}
              />
            </div>
          </div>

          {/* Track 2 */}
          {track2Enabled && (
            <div className="flex gap-6 items-center flex-wrap">
              <div className="w-48">
                <input
                  placeholder="TSLA"
                  type="text"
                  value={ticker2}
                  onChange={(e) => setTicker2(e.target.value)}
                />
              </div>

              <div className="flex gap-1 items-center">
                {CANDLE_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    title={color}
                    onClick={() => setYRisingColor2(color)}
                    style={{ backgroundColor: color }}
                    className={`w-4 h-4 rounded-full border-2 ${
                      yRisingColor2 === color
                        ? "border-gray-800"
                        : "border-transparent"
                    }`}
                  />
                ))}
              </div>
            </div>
          )}

          <button className="btn btn-default hidden" type="submit">
            Load
          </button>
        </form>

        {yDisplayInst1 && (yBothLoaded || yIsLoading) && (
          <Plotter
            key={`y-${yDisplayInst1.search_main_text}-${yDisplayInst2?.search_main_text ?? "none"}-${yResolutionIdx}`}
            data={yPlotData}
            instrument={yDisplayInst1}
            instrument2={track2Enabled ? yDisplayInst2 : null}
            resolution={yResolution}
            isCompareMode={track2Enabled}
            isLoading={yIsLoading}
            compress={compress}
            refreshCount={yRefreshCount}
            lastPrice={yDetails?.extended_price ?? yDetails?.last}
            alertPrice={yAlertPairId ? (priceAlert?.target_price ?? null) : null}
            onPriceClick={alertArmed && yAlertPairId ? handleChartPriceClick : undefined}
          />
        )}

        {yDetails && <DetailsBar details={yDetails} />}
      </div>
    </div>
  );
};

const DetailsBar = ({ details: d }: { details: InvcomDetails }) => {
  const toNum = (val: any): number | null => {
    if (val == null) return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
  };

  const changeColor = (_val: any) => "";

  const fmt = (val: any, decimals = 2) => {
    const n = toNum(val);
    return n == null ? "—" : n.toFixed(decimals);
  };

  const fmtChange = (val: any) => {
    const n = toNum(val);
    return n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2);
  };

  const fmtPct = (val: any) => {
    const n = toNum(val);
    return n == null ? "—" : (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
  };

const Row = ({ label, value, className = "" }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className={`flex gap-1 ${className}`}>
      <span className="text-gray-500">{label}:</span>
      <span>{value}</span>
    </div>
  );

  return (
    <div className="mt-2 text-xs text-black border-t border-gray-300 pt-2 flex gap-8">
      {/* Column 1 */}
      <div className="flex flex-col gap-1">
        <Row
          label="Change"
          value={<span className={changeColor(d.change_val)}>{fmtChange(d.change_val)}</span>}
        />
        <Row
          label="Change (%)"
          value={<span className={changeColor(d.change_percent_val)}>{fmtPct(d.change_percent_val)}</span>}
        />

        <div className="mt-1" />

        <Row label="Exchange" value={d.exchange_name ?? "—"} />
        <Row
          label="Exchange open"
          value={d.exchange_is_open == null ? "—" : String(d.exchange_is_open)}
        />

        <div className="mt-1" />

        <Row
          label="Extended change"
          value={<span className={changeColor(d.extended_change)}>{fmtChange(d.extended_change)}</span>}
        />
        <Row
          label="Extended change (%)"
          value={
            <span className={changeColor(d.extended_change_percent)}>
              {fmtPct(d.extended_change_percent)}
            </span>
          }
        />
        <Row label="Extended price" value={fmt(d.extended_price)} />
      </div>

      {/* Column 2 */}
      <div className="flex flex-col gap-1">
        <Row label="Last" value={fmt(d.last)} />
        <Row label="Open" value={fmt(d.open)} />
        <Row label="High" value={fmt(d.high)} />
        <Row label="Low" value={fmt(d.low)} />
        <Row label="Volume" value={d.volume ?? "—"} />

        <div className="mt-1" />

        <Row label="Last close" value={fmt(d.last_close_value)} />
        <Row label="Last time" value={d.last_timestamp ?? "—"} />
        <Row label="Market status" value={d.market_status ?? "—"} />
        <Row label="Next Earnings" value={d.next_earning_unix_time ?? "—"} />
      </div>
    </div>
  );
};

const Plotter = (props: {
  data: RtChartData | null;
  instrument: Instrument;
  instrument2: Instrument | null;
  resolution: string;
  isCompareMode: boolean;
  isLoading: boolean;
  compress: boolean;
  lastPrice?: number | null;
  alertPrice?: number | null;
  onPriceClick?: (price: number) => void;
  refreshCount?: number;
}) => {
  const [barToTheRight, setBarToTheRight] = useState(false);
  const data = props.data;

  return (
    <div>
      <div className="mt-4 font-semibold">
        [{props.instrument.search_main_text}] {props.instrument.search_main_longtext}
        {props.instrument.search_main_subtext
          ? ` (${props.instrument.search_main_subtext})`
          : ""}
        {props.instrument2 && (
          <>
            {" "}vs [{props.instrument2.search_main_text}] {props.instrument2.search_main_longtext}
            {props.instrument2.search_main_subtext
              ? ` (${props.instrument2.search_main_subtext})`
              : ""}
          </>
        )}
        {" "}· Res: {props.resolution}
        {props.isLoading && <span className="text-black font-normal"> (Loading...)</span>}
        {props.isCompareMode && " · % normalized"}
        {barToTheRight && (
          <span className="ml-2 text-red-600">Bar to the right!</span>
        )}
      </div>
      {!data && !props.isLoading && (
        <p className="text-gray-500">Data array empty</p>
      )}

      {data && (
        <RealtimeLwChart
          data={data}
          resolution={props.resolution}
          compress={props.compress}
          // dashed last-price and price-alert lines only on the absolute-price chart
          lastPrice={props.isCompareMode ? null : props.lastPrice}
          alertPrice={props.isCompareMode ? null : props.alertPrice}
          onPriceClick={props.onPriceClick}
          refreshCount={props.refreshCount}
          onBarHiddenRight={setBarToTheRight}
        />
      )}
    </div>
  );
};
